/**
 * Locale-aware formatting utilities.
 *
 * These helpers wrap the built-in `Intl` APIs so every date, number, and
 * currency value rendered in the UI respects the active locale.
 */

import type { Locale } from '@/translations';

// ---------------------------------------------------------------------------
// Locale mapping
// ---------------------------------------------------------------------------

/** Map our internal locale codes to BCP-47 tags accepted by `Intl`. */
const localeTag: Record<Locale, string> = {
  en: 'en-US',
  ar: 'ar-SA',
};

// ---------------------------------------------------------------------------
// Date & Time
// ---------------------------------------------------------------------------

/**
 * Format a date value using `Intl.DateTimeFormat`.
 *
 * @param date  - Date, timestamp (ms), or ISO string
 * @param locale - The active locale ('en' | 'ar')
 * @param options - Optional `Intl.DateTimeFormatOptions`
 * @returns Formatted date string (e.g. "Feb 9, 2026" or "٩ فبراير ٢٠٢٦")
 */
export function formatDate(
  date: Date | string | number | null | undefined,
  locale: Locale,
  options?: Intl.DateTimeFormatOptions,
): string {
  if (date == null) return '';
  const d = date instanceof Date ? date : new Date(date);
  if (isNaN(d.getTime())) return '';
  const defaults: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    ...options,
  };
  return new Intl.DateTimeFormat(localeTag[locale], defaults).format(d);
}

/**
 * Format a date + time value.
 */
export function formatDateTime(
  date: Date | string | number | null | undefined,
  locale: Locale,
  options?: Intl.DateTimeFormatOptions,
): string {
  if (date == null) return '';
  const d = date instanceof Date ? date : new Date(date);
  if (isNaN(d.getTime())) return '';
  const defaults: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    ...options,
  };
  return new Intl.DateTimeFormat(localeTag[locale], defaults).format(d);
}

/**
 * Format time only (hours & minutes).
 */
export function formatTime(
  date: Date | string | number | null | undefined,
  locale: Locale,
): string {
  if (date == null) return '';
  const d = date instanceof Date ? date : new Date(date);
  if (isNaN(d.getTime())) return '';
  return new Intl.DateTimeFormat(localeTag[locale], {
    hour: '2-digit',
    minute: '2-digit',
  }).format(d);
}

/**
 * Format a date using the long weekday + full date pattern.
 * (e.g. "Monday, February 9, 2026")
 */
export function formatFullDate(
  date: Date | string | number | null | undefined,
  locale: Locale,
): string {
  if (date == null) return '';
  const d = date instanceof Date ? date : new Date(date);
  if (isNaN(d.getTime())) return '';
  return new Intl.DateTimeFormat(localeTag[locale], {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  }).format(d);
}

/**
 * Format a relative time (e.g. "2 hours ago", "قبل ساعتين").
 * Falls back to a formatted date if `Intl.RelativeTimeFormat` is unavailable.
 */
export function formatRelativeTime(
  date: Date | string | number | null | undefined,
  locale: Locale,
): string {
  if (date == null) return '';
  const d = date instanceof Date ? date : new Date(date);
  if (isNaN(d.getTime())) return '';

  const now = Date.now();
  const diffMs = d.getTime() - now;
  const absDiff = Math.abs(diffMs);

  // Choose the best unit
  const seconds = Math.round(absDiff / 1_000);
  const minutes = Math.round(absDiff / 60_000);
  const hours = Math.round(absDiff / 3_600_000);
  const days = Math.round(absDiff / 86_400_000);

  const rtf = new Intl.RelativeTimeFormat(localeTag[locale], { numeric: 'auto' });
  const sign = diffMs < 0 ? -1 : 1;

  if (seconds < 60) return rtf.format(sign * seconds, 'second');
  if (minutes < 60) return rtf.format(sign * minutes, 'minute');
  if (hours < 24) return rtf.format(sign * hours, 'hour');
  if (days < 30) return rtf.format(sign * days, 'day');

  // Beyond 30 days, just return a formatted date
  return formatDate(d, locale);
}

// ---------------------------------------------------------------------------
// Numbers
// ---------------------------------------------------------------------------

/**
 * Format a number using `Intl.NumberFormat`.
 */
export function formatNumber(
  value: number | null | undefined,
  locale: Locale,
  options?: Intl.NumberFormatOptions,
): string {
  if (value == null) return '';
  return new Intl.NumberFormat(localeTag[locale], options).format(value);
}

// ---------------------------------------------------------------------------
// Currency
// ---------------------------------------------------------------------------

/** New Saudi Riyal sign (Unicode 17.0, U+20C1). */
export const SAR_SYMBOL = '\u20C1';

/**
 * Map an ISO 4217 currency code to its display symbol.
 * Returns the new Saudi Riyal symbol for SAR; passes other codes through.
 */
export function currencySymbol(code: string = 'SAR'): string {
  if (code.toUpperCase() === 'SAR') return SAR_SYMBOL;
  return code;
}

/**
 * Format a monetary amount.
 *
 * Uses number formatting with the new Saudi Riyal symbol (⃁) instead of the
 * legacy "SAR" ISO code so the UI complies with SAMA guidelines.
 *
 * @param amount   - The numeric amount
 * @param locale   - The active locale
 * @param currency - ISO 4217 currency code (default: 'SAR')
 */
export function formatCurrency(
  amount: number | null | undefined,
  locale: Locale,
  currency: string = 'SAR',
): string {
  if (amount == null) return '';
  const formatted = new Intl.NumberFormat(localeTag[locale], {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(amount);
  return `${currencySymbol(currency)} ${formatted}`;
}
